import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, type ButtonProps } from '~/ui/Button.js';

/**
 * The submit chip on every Settings form: off while its request is out, with a
 * word that says so.
 *
 * Four forms drew this by hand (a row's Save, create account, sign in, delete),
 * which is how the progress word drifts. `busy` turns the chip off and marks
 * it `aria-busy`; `busyLabel` is the word, `common.working` unless the form has
 * a truer one ("Deleting…"). Putting focus back after a refusal is the form's,
 * because only the form knows which field was refused.
 *
 * Settings-only on purpose. `Button` itself stays without a busy state until
 * the rest of the app's copies (`AuthScreen`, `ImportPrompt`, `LobbyScreen`,
 * `ProfileScreen`) are moved over and the look is decided once.
 */
export function SubmitButton({
  busy,
  busyLabel,
  disabled = false,
  variant = 'primary',
  size,
  children,
}: {
  readonly busy: boolean;
  readonly busyLabel?: string | undefined;
  /** Off for a reason other than a request in the air, such as an unconfirmed word. */
  readonly disabled?: boolean;
  readonly variant?: ButtonProps['variant'];
  readonly size?: ButtonProps['size'];
  readonly children: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Button
      type="submit"
      variant={variant}
      {...(size === undefined ? {} : { size })}
      disabled={busy || disabled}
      aria-busy={busy}
    >
      {busy ? (busyLabel ?? t('common.working')) : children}
    </Button>
  );
}
